import type React from "react";
import { useMemo, useState } from "react";

import { faCircleDollar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Typography } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useGetTherapistCommissions } from "api/services/therapist/hooks";
import { NoGraphData } from "routes/admin/components/NoGraphData";
import { useBars } from "routes/admin/components/useBars";
import { useFilterOptions } from "routes/admin/hooks/useFilterOptions";
import { StackedBarChart } from "shared/atoms/Charts";
import { MUISelect } from "shared/atoms/MUI";
import { GraphSkeleton } from "shared/atoms/SkeletonLayout/components/GraphSkeleton";
import { Spinner } from "shared/atoms/Spinner";
import { WidgetBox } from "shared/containers/WidgetBox";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars";
import { translateCurrency } from "utils/translations/translateCurrency";

export const Commissions: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { defaultMonthValue, monthOptions } = useFilterOptions();
  const [startTime, setStartTime] = useState(defaultMonthValue);

  const lastYearStartTime = monthOptions[monthOptions.length - 1].value;
  const { data: lastYearData, isLoading: isCheckingLastYear } = useGetTherapistCommissions({
    params: { start_time: lastYearStartTime },
  });
  const lastYearItems = lastYearData?.items || {};
  const hasLastYearCommissions = Object.values(lastYearItems).some(category =>
    Object.values(category).some(amount => amount > 0)
  );

  const { data: commissionsData, isLoading } = useGetTherapistCommissions({
    params: { start_time: startTime },
  });
  const earnings = commissionsData?.items || {};

  const currency = commissionsData?.currency;
  const itemsExist = Object.values(earnings).some(category => Object.values(category).some(amount => amount > 0));

  const { bars } = useBars();

  const chartData = useMemo(
    () =>
      Object.entries(earnings).map(([date, categories]) => {
        const dataPoint: Record<string, string | number> = { date };

        // Initialize all earning types to 0
        bars.forEach(bar => {
          dataPoint[bar.dataKey] = 0;
        });

        Object.entries(categories).forEach(([key, value]) => {
          dataPoint[key] = value;
        });

        return dataPoint;
      }),
    [earnings, bars]
  );

  const stackTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    chartData.forEach(dataPoint => {
      const date = dataPoint.date as string;
      totals[date] = bars.reduce((sum, bar) => sum + (Number(dataPoint[bar.dataKey]) || 0), 0);
    });
    return totals;
  }, [bars, chartData]);

  const grandTotal = useMemo(() => Object.values(stackTotals).reduce((sum, val) => sum + val, 0), [stackTotals]);

  if (isCheckingLastYear || !hasLastYearCommissions) {
    return null;
  }

  return (
    <WidgetBox
      title={t("admin.earnings.title")}
      description={t("admin.earnings.description")}
      icon={<FontAwesomeIcon icon={faCircleDollar} />}
      color="purple"
      toolbar={
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <MUISelect
            options={monthOptions}
            disabled={isLoading}
            value={startTime}
            onChange={e => setStartTime(e.target.value as string)}
            size="small"
            sx={{ minWidth: 160 }}
          />

          {!isLoading && commissionsData !== undefined && (
            <Typography variant="h2">{translateCurrency(convertCentsToDollarsNumber(grandTotal), currency)}</Typography>
          )}
          {isLoading && <Spinner small />}
        </Stack>
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "240px" }}>
        {isLoading && <GraphSkeleton height={340} />}
        {!isLoading && !itemsExist && <NoGraphData title={t("admin.earnings.no_data")} />}
        {!isLoading && itemsExist && (
          <StackedBarChart
            data={chartData}
            xAxisDataKey="date"
            xAxisProps={{
              tickFormatter: value => {
                const date = parseISO(value);
                return isValid(date) ? format(date, t("common.date.longNoDay")).toLocaleUpperCase() : "";
              },
              fontSize: 10,
              interval: "preserveStartEnd",
            }}
            yAxisProps={{
              tickFormatter: value => translateCurrency(convertCentsToDollarsNumber(Number(value)), currency),
            }}
            bars={bars}
            tooltipProps={{
              color: theme.colors.redesign.p100,
              labelFormatter: (label: string) => {
                const date = parseISO(label);
                return isValid(date) ? format(date, t("common.date.longNoDay")) : "";
              },
              stackTotals,
              valueFormatter: (value: number) => translateCurrency(convertCentsToDollarsNumber(value), currency),
            }}
          />
        )}
      </Box>
    </WidgetBox>
  );
};
